import React, { useMemo } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import {
  useListQuery,
  useUpdateMutation,
  useDeleteMutation,
  useUpdatePatchMutation,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';

interface Tenant {
  id: string;
  displayName: string;
  name: string;
  identifier: string;
  subdomain: string;
  isSuspended: boolean;
  isTrial: boolean;
}

const TenantsList: React.FC = () => {
  const { data, isLoading, isError, error } = useListQuery<Tenant>(
    `${API_ENDPOINTS.SAAS.TENANTS.BASE}`,
  );

  const tenants: Tenant[] = useMemo(() => {
    if (!data) return [];
    return data.map((item: any) => ({
      id: item.id,
      displayName: item.displayName || '-',
      name: item.name || '-',
      identifier: item.identifier || '-',
      subdomain: item.subdomain || '-',
      isSuspended: item.isSuspended ?? false,
      isTrial: item.isTrial ?? false,
    }));
  }, [data]);

  const columns: ColumnDef<Tenant>[] = useMemo(
    () => [
      {
        header: 'Display Name',
        accessorKey: 'displayName',
        cell: ({ row }) => (
          <Link to={`/tenants/${row.original.id}`} className="text-dark fw-bold">
            {row.original.displayName}
          </Link>
        ),
      },
      {
        header: 'Name',
        accessorKey: 'name',
      },
      {
        header: 'Identifier',
        accessorKey: 'identifier',
      },
      // {
      //   header: 'Subdomain',
      //   accessorKey: 'subdomain',
      // },
      {
        header: 'Status',
        accessorKey: 'isSuspended',
        cell: ({ row }) => (
          <span>
            {row.original.isSuspended ? 'Suspended' : ' Active'}
            {row.original.isTrial && ' (Trial)'}
          </span>
        ),
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }) => {
          const { id, isSuspended } = row.original;
          const { mutate: toggleSuspend } = useUpdatePatchMutation(
            `${API_ENDPOINTS.SAAS.TENANTS.BASE}/${id}`,
            API_ENDPOINTS.SAAS.TENANTS.BASE,
          );

          const { mutate: deleteTenant } = useDeleteMutation(
            `${API_ENDPOINTS.SAAS.TENANTS.BASE}/${id}`,
            API_ENDPOINTS.SAAS.TENANTS.BASE,
          );

          const handleToggleSuspend = () => {
            if (
              window.confirm(
                `Are you sure you want to ${isSuspended ? 'activate' : 'suspend'} this tenant?`,
              )
            ) {
              toggleSuspend({ data: { isSuspended: !isSuspended }, params: { id } });
            }
          };

          const handleDelete = () => {
            if (window.confirm('Are you sure you want to delete this tenant?')) {
              deleteTenant(id);
            }
          };

          return (
            <div className="d-flex gap-2 align-items-center">
              <i
                className={`bx ${isSuspended ? 'bx-play' : 'bx-pause'} ${
                  isSuspended ? 'text-success' : 'text-warning'
                } cursor-pointer`}
                title={isSuspended ? 'Unsuspend' : 'Suspend'}
                style={{ fontSize: '1.2rem' }}
                onClick={handleToggleSuspend}
              ></i>
              <i
                className="bx bx-trash text-muted cursor-pointer"
                title="Delete"
                style={{ fontSize: '1.2rem' }}
                onClick={handleDelete}
              ></i>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Tenants" breadcrumbItem="List" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : isError ? (
                  <Alert color="danger">
                    {error?.message || 'Failed to load tenants. Please try again later.'}
                  </Alert>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={tenants}
                    isGlobalFilter={true}
                    isAddButton={false}
                    isPagination={true}
                    isCustomPageSize={true}
                    buttonClass="btn btn-success btn-rounded"
                    buttonName="Add Tenant"
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    paginationClass="pagination"
                    SearchPlaceholder="Search tenants..."
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TenantsList;
